import type { Radians } from "@excalidraw/math";

import type { LibraryItem } from "../types";

export type PremiumLibraryItem = LibraryItem & {
  tier: "base" | "premium";
  category: string;
  tags?: string[];
  preview?: string;
};

export type LibraryCategory = {
  id: string;
  name: string;
  description?: string;
  icon?: string;
  items: PremiumLibraryItem[];
};

// Simplified hardcoded library items with minimal element data
export const premiumLibraryCategories: LibraryCategory[] = [

  {
    id: "text",
    name: "Text styles",
    description: "Text blocks and typography",
    icon: "📝",
    items: [
      {
        id: "text-heading-1",
        status: "published" as const,
        tier: "base" as const,
        category: "text",
        name: "Heading 1",
        created: Date.now(),
        elements: [
          {
            id: "9sc8O1DU3UW4Hm-0b2xRv",
            type: "text",
            x: 0,
            y: 0,
            width: 229.29591369628906,
            height: 60,
            angle: 0 as Radians,
            strokeColor: "#1e1e1e",
            backgroundColor: "#e9ecef",
            fillStyle: "solid",
            strokeWidth: 2,
            strokeStyle: "solid",
            roughness: 0,
            opacity: 100,
            groupIds: [],
            frameId: null,
            index: "a1" as any,
            roundness: null,
            seed: 2000966315,
            version: 1,
            versionNonce: 1,
            isDeleted: false,
            boundElements: null,
            updated: 1,
            link: null,
            locked: false,
            text: "Heading 1",
            fontSize: 48,
            fontFamily: 11,
            textAlign: "left",
            verticalAlign: "top",
            containerId: null,
            originalText: "Heading 1",
            autoResize: true,
            lineHeight: 1.25,
          } as any,
        ],
        tags: ["text", "heading"],
      },
      {
        id: "text-heading-2",
        status: "published" as const,
        tier: "base" as const,
        category: "text",
        name: "Heading 2",
        created: Date.now(),
        elements: [
          {
            id: "sjv3Qt0K1CmRJguml87F8",
            type: "text",
            x: 0,
            y: 0,
            width: 143.3099365234375,
            height: 37.5,
            angle: 0 as Radians,
            strokeColor: "#1e1e1e",
            backgroundColor: "#e9ecef",
            fillStyle: "solid",
            strokeWidth: 2,
            strokeStyle: "solid",
            roughness: 0,
            opacity: 100,
            groupIds: [],
            frameId: null,
            index: "a2" as any,
            roundness: null,
            seed: 1726417899,
            version: 1,
            versionNonce: 1,
            isDeleted: false,
            boundElements: null,
            updated: 1,
            link: null,
            locked: false,
            text: "Heading 2",
            fontSize: 30,
            fontFamily: 11,
            textAlign: "left",
            verticalAlign: "top",
            containerId: null,
            originalText: "Heading 2",
            autoResize: true,
            lineHeight: 1.25,
          } as any,
        ],
        tags: ["text", "heading"],
      },
      {
        id: "text-heading-3",
        status: "published" as const,
        tier: "base" as const,
        category: "text",
        name: "Heading 3",
        created: Date.now(),
        elements: [
          {
            id: "gilY1bSmOhHNWDLt7c9aA",
            type: "text",
            x: 0,
            y: 0,
            width: 114.64793395996094,
            height: 30,
            angle: 0 as Radians,
            strokeColor: "#1e1e1e",
            backgroundColor: "#e9ecef",
            fillStyle: "solid",
            strokeWidth: 2,
            strokeStyle: "solid",
            roughness: 0,
            opacity: 100,
            groupIds: [],
            frameId: null,
            index: "a3" as any,
            roundness: null,
            seed: 1979555819,
            version: 1,
            versionNonce: 1,
            isDeleted: false,
            boundElements: null,
            updated: 1,
            link: null,
            locked: false,
            text: "Heading 3",
            fontSize: 24,
            fontFamily: 11,
            textAlign: "left",
            verticalAlign: "top",
            containerId: null,
            originalText: "Heading 3",
            autoResize: true,
            lineHeight: 1.25,
          } as any,
        ],
        tags: ["text", "heading"],
      },
      {
        id: "text-heading-4",
        status: "published" as const,
        tier: "base" as const,
        category: "text",
        name: "Heading 4",
        created: Date.now(),
        elements: [
          {
            id: "tidjxPkWidO2cTtyP3tav",
            type: "text",
            x: 0,
            y: 0,
            width: 95.53994750976562,
            height: 25,
            angle: 0 as Radians,
            strokeColor: "#1e1e1e",
            backgroundColor: "#e9ecef",
            fillStyle: "solid",
            strokeWidth: 2,
            strokeStyle: "solid",
            roughness: 0,
            opacity: 100,
            groupIds: [],
            frameId: null,
            index: "a4" as any,
            roundness: null,
            seed: 730328549,
            version: 1,
            versionNonce: 1,
            isDeleted: false,
            boundElements: null,
            updated: 1,
            link: null,
            locked: false,
            text: "Heading 4",
            fontSize: 20,
            fontFamily: 11,
            textAlign: "left",
            verticalAlign: "top",
            containerId: null,
            originalText: "Heading 4",
            autoResize: true,
            lineHeight: 1.25,
          } as any,
        ],
        tags: ["text", "heading"],
      },
      {
        id: "text-large-text",
        status: "published" as const,
        tier: "base" as const,
        category: "text",
        name: "Large Text",
        created: Date.now(),
        elements: [
          {
            id: "cca4t7sjZ6b1Q65-9OxZ3",
            type: "text",
            x: 0,
            y: 0,
            width: 88.59593200683594,
            height: 22.5,
            angle: 0 as Radians,
            strokeColor: "#1e1e1e",
            backgroundColor: "#e9ecef",
            fillStyle: "solid",
            strokeWidth: 2,
            strokeStyle: "solid",
            roughness: 0,
            opacity: 100,
            groupIds: [],
            frameId: null,
            index: "a5" as any,
            roundness: null,
            seed: 663099877,
            version: 1,
            versionNonce: 1,
            isDeleted: false,
            boundElements: null,
            updated: 1,
            link: null,
            locked: false,
            text: "Large Text",
            fontSize: 18,
            fontFamily: 11,
            textAlign: "left",
            verticalAlign: "top",
            containerId: null,
            originalText: "Large Text",
            autoResize: true,
            lineHeight: 1.25,
          } as any,
        ],
        tags: ["text"],
      },
      {
        id: "text-lead-text",
        status: "published" as const,
        tier: "base" as const,
        category: "text",
        name: "Lead Text",
        created: Date.now(),
        elements: [
          {
            id: "hjP-s8koSgxV2ILHKZWLZ",
            type: "text",
            x: 0,
            y: 0,
            width: 79.30795288085938,
            height: 22.5,
            angle: 0 as Radians,
            strokeColor: "#1e1e1e",
            backgroundColor: "#e9ecef",
            fillStyle: "solid",
            strokeWidth: 2,
            strokeStyle: "solid",
            roughness: 0,
            opacity: 100,
            groupIds: [],
            frameId: null,
            index: "a6" as any,
            roundness: null,
            seed: 34294277,
            version: 1,
            versionNonce: 1,
            isDeleted: false,
            boundElements: null,
            updated: 1,
            link: null,
            locked: false,
            text: "Lead Text",
            fontSize: 18,
            fontFamily: 6,
            textAlign: "left",
            verticalAlign: "top",
            containerId: null,
            originalText: "Lead Text",
            autoResize: true,
            lineHeight: 1.25,
          } as any,
        ],
        tags: ["text"],
      },
      {
        id: "text-body",
        status: "published" as const,
        tier: "base" as const,
        category: "text",
        name: "Text",
        created: Date.now(),
        elements: [
          {
            id: "DOFH336M3ilLSBk2Dujj_",
            type: "text",
            x: 0,
            y: 0,
            width: 31.167984008789062,
            height: 20,
            angle: 0 as Radians,
            strokeColor: "#1e1e1e",
            backgroundColor: "#e9ecef",
            fillStyle: "solid",
            strokeWidth: 2,
            strokeStyle: "solid",
            roughness: 0,
            opacity: 100,
            groupIds: [],
            frameId: null,
            index: "a7" as any,
            roundness: null,
            seed: 1272933483,
            version: 1,
            versionNonce: 1,
            isDeleted: false,
            boundElements: null,
            updated: 1,
            link: null,
            locked: false,
            text: "Text",
            fontSize: 16,
            fontFamily: 6,
            textAlign: "left",
            verticalAlign: "top",
            containerId: null,
            originalText: "Text",
            autoResize: true,
            lineHeight: 1.25,
          } as any,
        ],
        tags: ["text"],
      },
      {
        id: "text-blockquote",
        status: "published" as const,
        tier: "base" as const,
        category: "text",
        name: "Blockquote",
        created: Date.now(),
        elements: [
          {
            id: "j5vIILS0AfI7TapHT7eYM",
            type: "text",
            x: 0,
            y: 0,
            width: 92.92791748046875,
            height: 20,
            angle: 0 as Radians,
            strokeColor: "#1e1e1e",
            backgroundColor: "#e9ecef",
            fillStyle: "solid",
            strokeWidth: 2,
            strokeStyle: "solid",
            roughness: 0,
            opacity: 100,
            groupIds: [],
            frameId: null,
            index: "a8" as any,
            roundness: null,
            seed: 1121189995,
            version: 1,
            versionNonce: 1,
            isDeleted: false,
            boundElements: null,
            updated: 1,
            link: null,
            locked: false,
            text: "\"blockquote\"",
            fontSize: 16,
            fontFamily: 6,
            textAlign: "left",
            verticalAlign: "top",
            containerId: null,
            originalText: "\"blockquote\"",
            autoResize: true,
            lineHeight: 1.25,
          } as any,
        ],
        tags: ["text", "blockquote"],
      },
    ],
  },
  {
    id: "buttons",
    name: "Buttons",
    description: "Interactive button elements",
    icon: "🔘",
    items: [
      {
        id: "button-primary",
        status: "published" as const,
        tier: "base" as const,
        category: "buttons",
        name: "Primary Button",
        created: Date.now(),
        elements: [
          {
            id: "5c6JIEgDTZUtPsWgtORef",
            type: "frame",
            x: 0,
            y: 0,
            width: 120,
            height: 40,
            angle: 0 as Radians,
            strokeColor: "transparent",
            backgroundColor: "#1e1e1e",
            fillStyle: "solid",
            strokeWidth: 2,
            strokeStyle: "solid",
            roughness: 0,
            opacity: 100,
            groupIds: [],
            frameId: null,
            index: "b1" as any,
            roundness: { type: 3, value: 12 } as any,
            seed: 775061419,
            version: 1,
            versionNonce: 1,
            isDeleted: false,
            boundElements: null,
            updated: 1,
            link: null,
            locked: false,
            name: null,
            showName: false,
            autoLayout: {
              enabled: true,
              direction: "horizontal",
              gap: 8,
              padding: {
                top: 10,
                right: 15,
                bottom: 10,
                left: 15,
              },
              alignmentPosition: "center-center",
              resizing: {
                width: "hug",
                height: "hug",
              },
            },
          } as any,
          {
            id: "OZi_uH4O2o3fv4IwzwExc",
            type: "text",
            x: 30,
            y: 10,
            width: 60,
            height: 20,
            angle: 0 as Radians,
            strokeColor: "#ffffff",
            backgroundColor: "transparent",
            fillStyle: "solid",
            strokeWidth: 1,
            strokeStyle: "solid",
            roughness: 0,
            opacity: 100,
            groupIds: [],
            frameId: "5c6JIEgDTZUtPsWgtORef",
            index: "b2" as any,
            roundness: null,
            seed: 1054061093,
            version: 1,
            versionNonce: 1,
            isDeleted: false,
            boundElements: null,
            updated: 1,
            link: null,
            locked: false,
            text: "Primary",
            fontSize: 16,
            fontFamily: 6,
            textAlign: "center",
            verticalAlign: "middle",
            containerId: null,
            originalText: "Primary",
            autoResize: true,
            lineHeight: 1.25,
          } as any,
        ],
        tags: ["button", "primary"],
      },
      {
        id: "button-secondary",
        status: "published" as const,
        tier: "base" as const,
        category: "buttons",
        name: "Secondary Button",
        created: Date.now(),
        elements: [
          {
            id: "WdaH7BU0dNYMj7i7a80NH",
            type: "frame",
            x: 0,
            y: 0,
            width: 120,
            height: 40,
            angle: 0 as Radians,
            strokeColor: "#ced4da",
            backgroundColor: "transparent",
            fillStyle: "solid",
            strokeWidth: 2,
            strokeStyle: "solid",
            roughness: 0,
            opacity: 100,
            groupIds: [],
            frameId: null,
            index: "b3" as any,
            roundness: { type: 3, value: 12 } as any,
            seed: 526382661,
            version: 1,
            versionNonce: 1,
            isDeleted: false,
            boundElements: null,
            updated: 1,
            link: null,
            locked: false,
            name: null,
            showName: false,
            autoLayout: {
              enabled: true,
              direction: "horizontal",
              gap: 8,
              padding: {
                top: 10,
                right: 15,
                bottom: 10,
                left: 15,
              },
              alignmentPosition: "center-center",
              resizing: {
                width: "hug",
                height: "hug",
              },
            },
          } as any,
          {
            id: "YrCxyp8BEtNcCQr3up1Ee",
            type: "text",
            x: 22,
            y: 10,
            width: 76,
            height: 20,
            angle: 0 as Radians,
            strokeColor: "#1e1e1e",
            backgroundColor: "transparent",
            fillStyle: "solid",
            strokeWidth: 1,
            strokeStyle: "solid",
            roughness: 0,
            opacity: 100,
            groupIds: [],
            frameId: "WdaH7BU0dNYMj7i7a80NH",
            index: "b4" as any,
            roundness: null,
            seed: 2138389643,
            version: 1,
            versionNonce: 1,
            isDeleted: false,
            boundElements: null,
            updated: 1,
            link: null,
            locked: false,
            text: "Secondary",
            fontSize: 16,
            fontFamily: 6,
            textAlign: "center",
            verticalAlign: "middle",
            containerId: null,
            originalText: "Secondary",
            autoResize: true,
            lineHeight: 1.25,
          } as any,
        ],
        tags: ["button", "secondary"],
      },
      {
        id: "button-destructive",
        status: "published" as const,
        tier: "base" as const,
        category: "buttons",
        name: "Destructive Button",
        created: Date.now(),
        elements: [
          {
            id: "B_IDf6-1zlGxeS-o8CdZY",
            type: "frame",
            x: 0,
            y: 0,
            width: 120,
            height: 40,
            angle: 0 as Radians,
            strokeColor: "transparent",
            backgroundColor: "#fa5252",
            fillStyle: "solid",
            strokeWidth: 2,
            strokeStyle: "solid",
            roughness: 0,
            opacity: 100,
            groupIds: [],
            frameId: null,
            index: "b5" as any,
            roundness: { type: 3, value: 12 } as any,
            seed: 1186526373,
            version: 1,
            versionNonce: 1,
            isDeleted: false,
            boundElements: null,
            updated: 1,
            link: null,
            locked: false,
            name: null,
            showName: false,
            autoLayout: {
              enabled: true,
              direction: "horizontal",
              gap: 8,
              padding: {
                top: 10,
                right: 15,
                bottom: 10,
                left: 15,
              },
              alignmentPosition: "center-center",
              resizing: {
                width: "hug",
                height: "hug",
              },
            },
          } as any,
          {
            id: "61rL3hlvSw0FLDJ_pcDcL",
            type: "text",
            x: 18,
            y: 10,
            width: 82,
            height: 20,
            angle: 0 as Radians,
            strokeColor: "#ffffff",
            backgroundColor: "transparent",
            fillStyle: "solid",
            strokeWidth: 1,
            strokeStyle: "solid",
            roughness: 0,
            opacity: 100,
            groupIds: [],
            frameId: "B_IDf6-1zlGxeS-o8CdZY",
            index: "b6" as any,
            roundness: null,
            seed: 1425658923,
            version: 1,
            versionNonce: 1,
            isDeleted: false,
            boundElements: null,
            updated: 1,
            link: null,
            locked: false,
            text: "Destructive",
            fontSize: 16,
            fontFamily: 6,
            textAlign: "center",
            verticalAlign: "middle",
            containerId: null,
            originalText: "Destructive",
            autoResize: true,
            lineHeight: 1.25,
          } as any,
        ],
        tags: ["button", "destructive"],
      },
      {
        id: "button-link",
        status: "published" as const,
        tier: "base" as const,
        category: "buttons",
        name: "Link Button",
        created: Date.now(),
        elements: [
          {
            id: "LinkButtonFrame",
            type: "frame",
            x: 0,
            y: 0,
            width: 90,
            height: 30,
            angle: 0 as Radians,
            strokeColor: "transparent",
            backgroundColor: "transparent",
            fillStyle: "solid",
            strokeWidth: 1,
            strokeStyle: "solid",
            roughness: 0,
            opacity: 100,
            groupIds: [],
            frameId: null,
            index: "b7" as any,
            roundness: null,
            seed: 1829147100,
            version: 1,
            versionNonce: 1,
            isDeleted: false,
            boundElements: null,
            updated: 1,
            link: null,
            locked: false,
            name: null,
            showName: false,
            autoLayout: {
              enabled: true,
              direction: "horizontal",
              gap: 4,
              padding: {
                top: 5,
                right: 8,
                bottom: 5,
                left: 8,
              },
              alignmentPosition: "center-center",
              resizing: {
                width: "hug",
                height: "hug",
              },
            },
          } as any,
          {
            id: "NR4wOa0x47eF3-tnu2ZSG",
            type: "text",
            x: 8,
            y: 5,
            width: 74,
            height: 20,
            angle: 0 as Radians,
            strokeColor: "#1e1e1e",
            backgroundColor: "transparent",
            fillStyle: "solid",
            strokeWidth: 1,
            strokeStyle: "solid",
            roughness: 0,
            opacity: 100,
            groupIds: [],
            frameId: "LinkButtonFrame",
            index: "b8" as any,
            roundness: null,
            seed: 1829147109,
            version: 1,
            versionNonce: 1,
            isDeleted: false,
            boundElements: null,
            updated: 1,
            link: null,
            locked: false,
            text: "Link Button",
            fontSize: 16,
            fontFamily: 6,
            textAlign: "center",
            verticalAlign: "middle",
            containerId: null,
            originalText: "Link Button",
            autoResize: true,
            lineHeight: 1.25,
          } as any,
        ],
        tags: ["button", "link"],
      },
      {
        id: "button-icon-text",
        status: "published" as const,
        tier: "base" as const,
        category: "buttons",
        name: "Icon Button",
        created: Date.now(),
        elements: [
          {
            id: "zumJQTerh1B2aTFVLYnh3",
            type: "frame",
            x: 0,
            y: 0,
            width: 140,
            height: 40,
            angle: 0 as Radians,
            strokeColor: "transparent",
            backgroundColor: "#ced4da",
            fillStyle: "solid",
            strokeWidth: 2,
            strokeStyle: "solid",
            roughness: 0,
            opacity: 100,
            groupIds: [],
            frameId: null,
            index: "b8" as any,
            roundness: { type: 3, value: 12 } as any,
            seed: 1599439883,
            version: 1,
            versionNonce: 1,
            isDeleted: false,
            boundElements: null,
            updated: 1,
            link: null,
            locked: false,
            name: null,
            showName: false,
            autoLayout: {
              enabled: true,
              direction: "horizontal",
              gap: 8,
              padding: {
                top: 10,
                right: 15,
                bottom: 10,
                left: 15,
              },
              alignmentPosition: "center-center",
              resizing: {
                width: "hug",
                height: "hug",
              },
            },
          } as any,
          {
            id: "EOsqGLYgLKLgpzMyoPdSo",
            type: "line",
            x: 15,
            y: 12,
            width: 0,
            height: 16,
            angle: 0 as Radians,
            strokeColor: "#868e96",
            backgroundColor: "#ffffff",
            fillStyle: "solid",
            strokeWidth: 2,
            strokeStyle: "solid",
            roughness: 0,
            opacity: 100,
            groupIds: [],
            frameId: "zumJQTerh1B2aTFVLYnh3",
            index: "b9a" as any,
            roundness: null,
            seed: 1286642806,
            version: 1,
            versionNonce: 1,
            isDeleted: false,
            boundElements: null,
            updated: 1,
            link: null,
            locked: false,
            points: [[0,0],[0,16]] as any,
            lastCommittedPoint: null,
            startBinding: null,
            endBinding: null,
            startArrowhead: null,
            endArrowhead: null,
          } as any,
          {
            id: "5BZJJwZY3LPK8VfuS7CkX",
            type: "line",
            x: 7,
            y: 20,
            width: 16,
            height: 0,
            angle: 0 as Radians,
            strokeColor: "#868e96",
            backgroundColor: "#ffffff",
            fillStyle: "solid",
            strokeWidth: 2,
            strokeStyle: "solid",
            roughness: 0,
            opacity: 100,
            groupIds: [],
            frameId: "zumJQTerh1B2aTFVLYnh3",
            index: "b9b" as any,
            roundness: null,
            seed: 680631734,
            version: 1,
            versionNonce: 1,
            isDeleted: false,
            boundElements: null,
            updated: 1,
            link: null,
            locked: false,
            points: [[0,0],[16,0]] as any,
            lastCommittedPoint: null,
            startBinding: null,
            endBinding: null,
            startArrowhead: null,
            endArrowhead: null,
          } as any,
          {
            id: "Kx8zYc0mdNIQ8lV0nJyyg",
            type: "text",
            x: 35,
            y: 10,
            width: 84,
            height: 20,
            angle: 0 as Radians,
            strokeColor: "#1e1e1e",
            backgroundColor: "transparent",
            fillStyle: "solid",
            strokeWidth: 1,
            strokeStyle: "solid",
            roughness: 0,
            opacity: 100,
            groupIds: [],
            frameId: "zumJQTerh1B2aTFVLYnh3",
            index: "b9" as any,
            roundness: null,
            seed: 1573752043,
            version: 1,
            versionNonce: 1,
            isDeleted: false,
            boundElements: null,
            updated: 1,
            link: null,
            locked: false,
            text: "Icon Button",
            fontSize: 16,
            fontFamily: 6,
            textAlign: "center",
            verticalAlign: "middle",
            containerId: null,
            originalText: "Icon Button",
            autoResize: true,
            lineHeight: 1.25,
          } as any,
        ],
        tags: ["button", "icon"],
      },
      {
        id: "button-icon-circle",
        status: "published" as const,
        tier: "base" as const,
        category: "buttons",
        name: "Icon Circle Button",
        created: Date.now(),
        elements: [
          {
            id: "IconCircleButtonFrame",
            type: "frame",
            x: 0,
            y: 0,
            width: 40,
            height: 40,
            angle: 0 as Radians,
            strokeColor: "#868e96",
            backgroundColor: "transparent",
            fillStyle: "solid",
            strokeWidth: 2,
            strokeStyle: "solid",
            roughness: 0,
            opacity: 100,
            groupIds: [],
            frameId: null,
            index: "b10" as any,
            roundness: { type: 2 } as any,
            seed: 1422411045,
            version: 1,
            versionNonce: 1,
            isDeleted: false,
            boundElements: null,
            updated: 1,
            link: null,
            locked: false,
            name: null,
            showName: false,
            autoLayout: {
              enabled: true,
              direction: "horizontal",
              gap: 0,
              padding: {
                top: 8,
                right: 8,
                bottom: 8,
                left: 8,
              },
              alignmentPosition: "center-center",
              resizing: {
                width: "fixed",
                height: "fixed",
              },
            },
          } as any,
          {
            id: "B7bkJ9YZ3dTNiC5KTLZde",
            type: "line",
            x: 20,
            y: 8,
            width: 0,
            height: 24,
            angle: 0 as Radians,
            strokeColor: "#868e96",
            backgroundColor: "#ffffff",
            fillStyle: "solid",
            strokeWidth: 2,
            strokeStyle: "solid",
            roughness: 0,
            opacity: 100,
            groupIds: [],
            frameId: "IconCircleButtonFrame",
            index: "b10a" as any,
            roundness: null,
            seed: 1848280554,
            version: 1,
            versionNonce: 1,
            isDeleted: false,
            boundElements: null,
            updated: 1,
            link: null,
            locked: false,
            points: [[0,0],[0,24]] as any,
            lastCommittedPoint: null,
            startBinding: null,
            endBinding: null,
            startArrowhead: null,
            endArrowhead: null,
          } as any,
          {
            id: "au8wpvqZma5HpG2SV5-Tt",
            type: "line",
            x: 8,
            y: 20,
            width: 24,
            height: 0,
            angle: 0 as Radians,
            strokeColor: "#868e96",
            backgroundColor: "#ffffff",
            fillStyle: "solid",
            strokeWidth: 2,
            strokeStyle: "solid",
            roughness: 0,
            opacity: 100,
            groupIds: [],
            frameId: "IconCircleButtonFrame",
            index: "b10b" as any,
            roundness: null,
            seed: 594950314,
            version: 1,
            versionNonce: 1,
            isDeleted: false,
            boundElements: null,
            updated: 1,
            link: null,
            locked: false,
            points: [[0,0],[24,0]] as any,
            lastCommittedPoint: null,
            startBinding: null,
            endBinding: null,
            startArrowhead: null,
            endArrowhead: null,
          } as any,
        ],
        tags: ["button", "icon", "circle"],
      },
    ],
  },
  {
    id: "forms",
    name: "Forms",
    description: "Form elements and inputs",
    icon: "📋",
    items: [
      {
        id: "form-tabs",
        status: "published" as const,
        tier: "base" as const,
        category: "forms",
        name: "Tabs",
        created: Date.now(),
        elements: [
          {
            id: "1ClhTXN9Nj8c1JmiPKpkI",
            type: "rectangle",
            x: 0,
            y: 0,
            width: 106.67,
            height: 40,
            angle: 0 as Radians,
            strokeColor: "#ced4da",
            backgroundColor: "#e9ecef",
            fillStyle: "solid",
            strokeWidth: 2,
            strokeStyle: "solid",
            roughness: 0,
            opacity: 100,
            groupIds: ["tabsGroup"],
            frameId: null,
            index: "f1" as any,
            roundness: { type: 6, corners: { topLeft: 12, topRight: 2, bottomLeft: 12, bottomRight: 0 } } as any,
            seed: 1185136886,
            version: 1,
            versionNonce: 1,
            isDeleted: false,
            boundElements: [{ type: "text", id: "MM_cHam6u6vCMuvhi0_yr" }],
            updated: 1,
            link: null,
            locked: false,
          } as any,
          {
            id: "MM_cHam6u6vCMuvhi0_yr",
            type: "text",
            x: 32,
            y: 10,
            width: 40,
            height: 20,
            angle: 0 as Radians,
            strokeColor: "#343a40",
            backgroundColor: "#e9ecef",
            fillStyle: "solid",
            strokeWidth: 1,
            strokeStyle: "solid",
            roughness: 0,
            opacity: 100,
            groupIds: ["tabsGroup"],
            frameId: null,
            index: "f1t" as any,
            roundness: null,
            seed: 925725034,
            version: 1,
            versionNonce: 1,
            isDeleted: false,
            boundElements: null,
            updated: 1,
            link: null,
            locked: false,
            text: "Tab 1",
            fontSize: 16,
            fontFamily: 11,
            textAlign: "center",
            verticalAlign: "middle",
            containerId: "1ClhTXN9Nj8c1JmiPKpkI",
            originalText: "Tab 1",
            autoResize: true,
            lineHeight: 1.25,
          } as any,
          {
            id: "PiOmNG3uvPF6X3Et9Oj_9",
            type: "rectangle",
            x: 106,
            y: 0,
            width: 106.67,
            height: 40,
            angle: 0 as Radians,
            strokeColor: "#ced4da",
            backgroundColor: "#ffffff",
            fillStyle: "solid",
            strokeWidth: 2,
            strokeStyle: "solid",
            roughness: 0,
            opacity: 100,
            groupIds: ["tabsGroup"],
            frameId: null,
            index: "f2" as any,
            roundness: null,
            seed: 539521898,
            version: 1,
            versionNonce: 1,
            isDeleted: false,
            boundElements: [{ type: "text", id: "ttMIvksTysakFEww8Sj0-" }],
            updated: 1,
            link: null,
            locked: false,
          } as any,
          {
            id: "ttMIvksTysakFEww8Sj0-",
            type: "text",
            x: 138,
            y: 10,
            width: 40,
            height: 20,
            angle: 0 as Radians,
            strokeColor: "#343a40",
            backgroundColor: "#e9ecef",
            fillStyle: "solid",
            strokeWidth: 1,
            strokeStyle: "solid",
            roughness: 0,
            opacity: 100,
            groupIds: ["tabsGroup"],
            frameId: null,
            index: "f2t" as any,
            roundness: null,
            seed: 727339062,
            version: 1,
            versionNonce: 1,
            isDeleted: false,
            boundElements: null,
            updated: 1,
            link: null,
            locked: false,
            text: "Tab 2",
            fontSize: 16,
            fontFamily: 6,
            textAlign: "center",
            verticalAlign: "middle",
            containerId: "PiOmNG3uvPF6X3Et9Oj_9",
            originalText: "Tab 2",
            autoResize: true,
            lineHeight: 1.25,
          } as any,
          {
            id: "K0VQKFRCcYHa-xqhYEE7c",
            type: "rectangle",
            x: 213,
            y: 0,
            width: 106.67,
            height: 40,
            angle: 0 as Radians,
            strokeColor: "#ced4da",
            backgroundColor: "#ffffff",
            fillStyle: "solid",
            strokeWidth: 2,
            strokeStyle: "solid",
            roughness: 0,
            opacity: 100,
            groupIds: ["tabsGroup"],
            frameId: null,
            index: "f3" as any,
            roundness: { type: 6, corners: { topLeft: 0, topRight: 12, bottomLeft: 0, bottomRight: 12 } } as any,
            seed: 1246386602,
            version: 1,
            versionNonce: 1,
            isDeleted: false,
            boundElements: [{ type: "text", id: "FB9nMsqVDBquyBO5O-CZr" }],
            updated: 1,
            link: null,
            locked: false,
          } as any,
          {
            id: "FB9nMsqVDBquyBO5O-CZr",
            type: "text",
            x: 245,
            y: 10,
            width: 40,
            height: 20,
            angle: 0 as Radians,
            strokeColor: "#343a40",
            backgroundColor: "#e9ecef",
            fillStyle: "solid",
            strokeWidth: 1,
            strokeStyle: "solid",
            roughness: 0,
            opacity: 100,
            groupIds: ["tabsGroup"],
            frameId: null,
            index: "f3t" as any,
            roundness: null,
            seed: 425782774,
            version: 1,
            versionNonce: 1,
            isDeleted: false,
            boundElements: null,
            updated: 1,
            link: null,
            locked: false,
            text: "Tab 3",
            fontSize: 16,
            fontFamily: 6,
            textAlign: "center",
            verticalAlign: "middle",
            containerId: "K0VQKFRCcYHa-xqhYEE7c",
            originalText: "Tab 3",
            autoResize: true,
            lineHeight: 1.25,
          } as any,
        ],
        tags: ["form", "tabs"],
      },
      {
        id: "form-input-text",
        status: "published" as const,
        tier: "base" as const,
        category: "forms",
        name: "Input Field",
        created: Date.now(),
        elements: [
          {
            id: "ZDahogPYJFZIEvyMS9ZtD",
            type: "text",
            x: 0,
            y: -25,
            width: 120,
            height: 20,
            angle: 0 as Radians,
            strokeColor: "#1e1e1e",
            backgroundColor: "#e9ecef",
            fillStyle: "solid",
            strokeWidth: 2,
            strokeStyle: "solid",
            roughness: 0,
            opacity: 100,
            groupIds: ["lrHhYqM2Hv1B2ENZFvRWx"],
            frameId: null,
            index: "inLbl" as any,
            roundness: null,
            seed: 840810622,
            version: 1,
            versionNonce: 1,
            isDeleted: false,
            boundElements: null,
            updated: 1,
            link: null,
            locked: false,
            text: "Input label",
            fontSize: 16,
            fontFamily: 6,
            textAlign: "left",
            verticalAlign: "top",
            containerId: null,
            originalText: "Input label",
            autoResize: true,
            lineHeight: 1.25,
          } as any,
          {
            id: "ez80IJSFqguuH5bG3-BIc",
            type: "rectangle",
            x: 0,
            y: 0,
            width: 320,
            height: 40,
            angle: 0 as Radians,
            strokeColor: "#ced4da",
            backgroundColor: "#ffffff",
            fillStyle: "solid",
            strokeWidth: 2,
            strokeStyle: "solid",
            roughness: 0,
            opacity: 100,
            groupIds: ["lrHhYqM2Hv1B2ENZFvRWx"],
            frameId: null,
            index: "inRect" as any,
            roundness: { type: 3, value: 12 } as any,
            seed: 572860414,
            version: 1,
            versionNonce: 1,
            isDeleted: false,
            boundElements: [{ type: "text", id: "QZeVfs9SJD1tCk2fikZOb" }],
            updated: 1,
            link: null,
            locked: false,
          } as any,
          {
            id: "QZeVfs9SJD1tCk2fikZOb",
            type: "text",
            x: 5,
            y: 10,
            width: 100,
            height: 20,
            angle: 0 as Radians,
            strokeColor: "#1e1e1e",
            backgroundColor: "#e9ecef",
            fillStyle: "solid",
            strokeWidth: 2,
            strokeStyle: "solid",
            roughness: 0,
            opacity: 100,
            groupIds: ["lrHhYqM2Hv1B2ENZFvRWx"],
            frameId: null,
            index: "inTxt" as any,
            roundness: null,
            seed: 1568372798,
            version: 1,
            versionNonce: 1,
            isDeleted: false,
            boundElements: null,
            updated: 1,
            link: null,
            locked: false,
            text: "  Input text",
            fontSize: 16,
            fontFamily: 6,
            textAlign: "left",
            verticalAlign: "middle",
            containerId: "ez80IJSFqguuH5bG3-BIc",
            originalText: "  Input text",
            autoResize: true,
            lineHeight: 1.25,
          } as any,
        ],
        tags: ["form", "input"],
      },
      {
        id: "form-checkbox",
        status: "published" as const,
        tier: "base" as const,
        category: "forms",
        name: "Checked",
        created: Date.now(),
        elements: [
          {
            id: "633bKukC3i9qBGUUyxMK0",
            type: "rectangle",
            x: 0,
            y: 0,
            width: 32,
            height: 32,
            angle: 0 as Radians,
            strokeColor: "#ced4da",
            backgroundColor: "#ffffff",
            fillStyle: "solid",
            strokeWidth: 2,
            strokeStyle: "solid",
            roughness: 0,
            opacity: 100,
            groupIds: ["uWOzlPjoUG9p433qPqIer"],
            frameId: null,
            index: "cb1" as any,
            roundness: { type: 3, value: 12 } as any,
            seed: 1874794986,
            version: 1,
            versionNonce: 1,
            isDeleted: false,
            boundElements: [{ type: "text", id: "qXHo-pu1dPJ2RM2oWKvk9" }],
            updated: 1,
            link: null,
            locked: false,
          } as any,
          {
            id: "qXHo-pu1dPJ2RM2oWKvk9",
            type: "text",
            x: 10,
            y: 6,
            width: 12,
            height: 20,
            angle: 0 as Radians,
            strokeColor: "#1e1e1e",
            backgroundColor: "#ffffff",
            fillStyle: "solid",
            strokeWidth: 1,
            strokeStyle: "solid",
            roughness: 0,
            opacity: 100,
            groupIds: ["uWOzlPjoUG9p433qPqIer"],
            frameId: null,
            index: "cb1t" as any,
            roundness: null,
            seed: 454058346,
            version: 1,
            versionNonce: 1,
            isDeleted: false,
            boundElements: null,
            updated: 1,
            link: null,
            locked: false,
            text: "✔",
            fontSize: 16,
            fontFamily: 6,
            textAlign: "center",
            verticalAlign: "middle",
            containerId: "633bKukC3i9qBGUUyxMK0",
            originalText: "✔",
            autoResize: true,
            lineHeight: 1.25,
          } as any,
          {
            id: "checkboxCheckedLabel",
            type: "text",
            x: 40,
            y: 6,
            width: 100,
            height: 20,
            angle: 0 as Radians,
            strokeColor: "#1e1e1e",
            backgroundColor: "#e9ecef",
            fillStyle: "solid",
            strokeWidth: 1,
            strokeStyle: "solid",
            roughness: 0,
            opacity: 100,
            groupIds: ["uWOzlPjoUG9p433qPqIer"],
            frameId: null,
            index: "cbLbl" as any,
            roundness: null,
            seed: 223344,
            version: 1,
            versionNonce: 1,
            isDeleted: false,
            boundElements: null,
            updated: 1,
            link: null,
            locked: false,
            text: "Checked",
            fontSize: 16,
            fontFamily: 6,
            textAlign: "left",
            verticalAlign: "middle",
            containerId: null,
            originalText: "Checked",
            autoResize: true,
            lineHeight: 1.25,
          } as any,
        ],
        tags: ["form", "checkbox"],
      },
      {
        id: "form-checkbox-unchecked",
        status: "published" as const,
        tier: "base" as const,
        category: "forms",
        name: "Unchecked",
        created: Date.now(),
        elements: [
          {
            id: "AP-ihPNLco9UF9ylgkpTJ",
            type: "rectangle",
            x: 0,
            y: 0,
            width: 32,
            height: 32,
            angle: 0 as Radians,
            strokeColor: "#ced4da",
            backgroundColor: "#ffffff",
            fillStyle: "solid",
            strokeWidth: 2,
            strokeStyle: "solid",
            roughness: 0,
            opacity: 100,
            groupIds: ["tERb86qFRJN1WH1Kpu1Jf"],
            frameId: null,
            index: "cbu1" as any,
            roundness: { type: 3, value: 12 } as any,
            seed: 888999,
            version: 1,
            versionNonce: 1,
            isDeleted: false,
            boundElements: null,
            updated: 1,
            link: null,
            locked: false,
          } as any,
          {
            id: "checkboxUncheckedLabel",
            type: "text",
            x: 40,
            y: 6,
            width: 100,
            height: 20,
            angle: 0 as Radians,
            strokeColor: "#1e1e1e",
            backgroundColor: "#e9ecef",
            fillStyle: "solid",
            strokeWidth: 1,
            strokeStyle: "solid",
            roughness: 0,
            opacity: 100,
            groupIds: ["tERb86qFRJN1WH1Kpu1Jf"],
            frameId: null,
            index: "cbuLbl" as any,
            roundness: null,
            seed: 112233,
            version: 1,
            versionNonce: 1,
            isDeleted: false,
            boundElements: null,
            updated: 1,
            link: null,
            locked: false,
            text: "Unchecked",
            fontSize: 16,
            fontFamily: 6,
            textAlign: "left",
            verticalAlign: "middle",
            containerId: null,
            originalText: "Unchecked",
            autoResize: true,
            lineHeight: 1.25,
          } as any,
        ],
        tags: ["form", "checkbox", "unchecked"],
      },
      {
        id: "form-radio",
        status: "published" as const,
        tier: "base" as const,
        category: "forms",
        name: "Selected",
        created: Date.now(),
        elements: [
          {
            id: "D2vpjJlDjzsICvsnEKghT",
            type: "ellipse",
            x: 0,
            y: 0,
            width: 32,
            height: 32,
            angle: 0 as Radians,
            strokeColor: "#ced4da",
            backgroundColor: "#ffffff",
            fillStyle: "solid",
            strokeWidth: 2,
            strokeStyle: "solid",
            roughness: 0,
            opacity: 100,
            groupIds: ["W7zVrBtPojTTpYi0DwMLD"],
            frameId: null,
            index: "rd1" as any,
            roundness: null,
            seed: 1247252074,
            version: 1,
            versionNonce: 1,
            isDeleted: false,
            boundElements: null,
            updated: 1,
            link: null,
            locked: false,
          } as any,
          {
            id: "RuvpId6Vx7uIMO-OtcdUG",
            type: "ellipse",
            x: 8,
            y: 8,
            width: 16,
            height: 16,
            angle: 0 as Radians,
            strokeColor: "transparent",
            backgroundColor: "#1e1e1e",
            fillStyle: "solid",
            strokeWidth: 2,
            strokeStyle: "solid",
            roughness: 0,
            opacity: 100,
            groupIds: ["W7zVrBtPojTTpYi0DwMLD"],
            frameId: null,
            index: "rd1i" as any,
            roundness: null,
            seed: 2048062454,
            version: 1,
            versionNonce: 1,
            isDeleted: false,
            boundElements: null,
            updated: 1,
            link: null,
            locked: false,
          } as any,
          {
            id: "radioSelectedLabel",
            type: "text",
            x: 40,
            y: 6,
            width: 100,
            height: 20,
            angle: 0 as Radians,
            strokeColor: "#1e1e1e",
            backgroundColor: "#e9ecef",
            fillStyle: "solid",
            strokeWidth: 1,
            strokeStyle: "solid",
            roughness: 0,
            opacity: 100,
            groupIds: ["W7zVrBtPojTTpYi0DwMLD"],
            frameId: null,
            index: "rdLbl" as any,
            roundness: null,
            seed: 556677,
            version: 1,
            versionNonce: 1,
            isDeleted: false,
            boundElements: null,
            updated: 1,
            link: null,
            locked: false,
            text: "Selected",
            fontSize: 16,
            fontFamily: 6,
            textAlign: "left",
            verticalAlign: "middle",
            containerId: null,
            originalText: "Selected",
            autoResize: true,
            lineHeight: 1.25,
          } as any,
        ],
        tags: ["form", "radio"],
      },
      {
        id: "form-radio-unselected",
        status: "published" as const,
        tier: "base" as const,
        category: "forms",
        name: "Unselected",
        created: Date.now(),
        elements: [
          {
            id: "ARk8rLjy9MwXXiDUcqMTc",
            type: "ellipse",
            x: 0,
            y: 0,
            width: 32,
            height: 32,
            angle: 0 as Radians,
            strokeColor: "#ced4da",
            backgroundColor: "#ffffff",
            fillStyle: "solid",
            strokeWidth: 2,
            strokeStyle: "solid",
            roughness: 0,
            opacity: 100,
            groupIds: ["_ySyycyrYr6p1O7-ac9jb"],
            frameId: null,
            index: "rdu1" as any,
            roundness: null,
            seed: 445566,
            version: 1,
            versionNonce: 1,
            isDeleted: false,
            boundElements: null,
            updated: 1,
            link: null,
            locked: false,
          } as any,
          {
            id: "radioUnselectedLabel",
            type: "text",
            x: 40,
            y: 6,
            width: 100,
            height: 20,
            angle: 0 as Radians,
            strokeColor: "#1e1e1e",
            backgroundColor: "#e9ecef",
            fillStyle: "solid",
            strokeWidth: 1,
            strokeStyle: "solid",
            roughness: 0,
            opacity: 100,
            groupIds: ["_ySyycyrYr6p1O7-ac9jb"],
            frameId: null,
            index: "rduLbl" as any,
            roundness: null,
            seed: 334455,
            version: 1,
            versionNonce: 1,
            isDeleted: false,
            boundElements: null,
            updated: 1,
            link: null,
            locked: false,
            text: "Unselected",
            fontSize: 16,
            fontFamily: 6,
            textAlign: "left",
            verticalAlign: "middle",
            containerId: null,
            originalText: "Unselected",
            autoResize: true,
            lineHeight: 1.25,
          } as any,
        ],
        tags: ["form", "radio", "unselected"],
      },
    ],
  },
  {
    id: "navigation",
    name: "Shapes",
    description: "Navigation and menu elements",
    icon: "🧭",
    items: [
      {
        id: "shape-triangle",
        status: "published" as const,
        tier: "base" as const,
        category: "navigation",
        name: "Triangle",
        created: Date.now(),
        elements: [
          {
            type: "line",
            version: 1,
            versionNonce: 1001,
            index: "sh1" as any,
            isDeleted: false,
            id: "triangle-1",
            fillStyle: "solid",
            strokeWidth: 2,
            strokeStyle: "solid",
            roughness: 0,
            opacity: 100,
            angle: 0 as Radians,
            x: 0,
            y: 0,
            strokeColor: "#000000",
            backgroundColor: "transparent",
            width: 80,
            height: 80,
            seed: 1001,
            groupIds: [],
            frameId: null,
            roundness: null,
            boundElements: null,
            updated: 1,
            link: null,
            locked: false,
            points: [[40,0],[80,80],[0,80],[40,0]],
            lastCommittedPoint: null,
            startBinding: null,
            endBinding: null,
            startArrowhead: null,
            endArrowhead: null,
          } as any,
        ],
        tags: ["shape", "triangle"],
      },
      {
        id: "shape-diamond",
        status: "published" as const,
        tier: "base" as const,
        category: "navigation",
        name: "Diamond",
        created: Date.now(),
        elements: [
          {
            type: "diamond",
            version: 1,
            versionNonce: 23,
            index: "a16" as any,
            isDeleted: false,
            id: "nav-diamond-1",
            fillStyle: "solid",
            strokeWidth: 2,
            strokeStyle: "solid",
            roughness: 0,
            opacity: 100,
            angle: 0 as Radians,
            x: 0,
            y: 0,
            strokeColor: "#000000",
            backgroundColor: "transparent",
            width: 80,
            height: 80,
            seed: 23,
            groupIds: [],
            frameId: null,
            roundness: null,
            boundElements: null,
            updated: 1,
            link: null,
            locked: false,
          } as any,
        ],
        tags: ["shape", "diamond"],
      },
      {
        id: "shape-hexagon",
        status: "published" as const,
        tier: "base" as const,
        category: "navigation",
        name: "Hexagon",
        created: Date.now(),
        elements: [
          {
            type: "line",
            version: 1,
            versionNonce: 24,
            index: "a17" as any,
            isDeleted: false,
            id: "nav-hexagon-1",
            fillStyle: "solid",
            strokeWidth: 2,
            strokeStyle: "solid",
            roughness: 0,
            opacity: 100,
            angle: 0 as Radians,
            x: 0,
            y: 0,
            strokeColor: "#000000",
            backgroundColor: "transparent",
            width: 80,
            height: 70,
            seed: 24,
            groupIds: [],
            frameId: null,
            roundness: null,
            boundElements: null,
            updated: 1,
            link: null,
            locked: false,
            points: [[20, 0], [60, 0], [80, 35], [60, 70], [20, 70], [0, 35], [20, 0]],
            lastCommittedPoint: null,
            startBinding: null,
            endBinding: null,
            startArrowhead: null,
            endArrowhead: null,
          } as any,
        ],
        tags: ["shape", "hexagon"],
      },
      {
        id: "shape-square",
        status: "published" as const,
        tier: "base" as const,
        category: "navigation",
        name: "Square",
        created: Date.now(),
        elements: [
          {
            type: "rectangle",
            version: 1,
            versionNonce: 1006,
            index: "sh2" as any,
            isDeleted: false,
            id: "square-1",
            fillStyle: "solid",
            strokeWidth: 2,
            strokeStyle: "solid",
            roughness: 0,
            opacity: 100,
            angle: 0 as Radians,
            x: 0,
            y: 0,
            strokeColor: "#000000",
            backgroundColor: "transparent",
            width: 80,
            height: 80,
            seed: 1006,
            groupIds: [],
            frameId: null,
            roundness: null,
            boundElements: null,
            updated: 1,
            link: null,
            locked: false,
          } as any,
        ],
        tags: ["shape", "square"],
      },
      {
        id: "shape-star",
        status: "published" as const,
        tier: "base" as const,
        category: "navigation",
        name: "Star",
        created: Date.now(),
        elements: [
          {
            type: "line",
            version: 1,
            versionNonce: 1003,
            index: "sh3" as any,
            isDeleted: false,
            id: "star-1",
            fillStyle: "solid",
            strokeWidth: 2,
            strokeStyle: "solid",
            roughness: 0,
            opacity: 100,
            angle: 0 as Radians,
            x: 0,
            y: 0,
            strokeColor: "#000000",
            backgroundColor: "transparent",
            width: 80,
            height: 80,
            seed: 1003,
            groupIds: [],
            frameId: null,
            roundness: null,
            boundElements: null,
            updated: 1,
            link: null,
            locked: false,
            points: [[40,0],[50,30],[80,30],[55,50],[65,80],[40,60],[15,80],[25,50],[0,30],[30,30],[40,0]],
            lastCommittedPoint: null,
            startBinding: null,
            endBinding: null,
            startArrowhead: null,
            endArrowhead: null,
          } as any,
        ],
        tags: ["shape", "star"],
      },
      {
        id: "shape-circle",
        status: "published" as const,
        tier: "base" as const,
        category: "navigation",
        name: "Circle",
        created: Date.now(),
        elements: [
          {
            type: "ellipse",
            version: 1,
            versionNonce: 1007,
            index: "sh4" as any,
            isDeleted: false,
            id: "circle-1",
            fillStyle: "solid",
            strokeWidth: 2,
            strokeStyle: "solid",
            roughness: 0,
            opacity: 100,
            angle: 0 as Radians,
            x: 0,
            y: 0,
            strokeColor: "#000000",
            backgroundColor: "transparent",
            width: 80,
            height: 80,
            seed: 1007,
            groupIds: [],
            frameId: null,
            roundness: null,
            boundElements: null,
            updated: 1,
            link: null,
            locked: false,
          } as any,
        ],
        tags: ["shape", "circle"],
      },
    ],
  },
];

export const getCategoriesWithCounts = () => {
  return premiumLibraryCategories.map((category) => ({
    ...category,
    baseCount: category.items.filter((item) => item.tier === "base").length,
    premiumCount: category.items.filter((item) => item.tier === "premium")
      .length,
  }));
};

export const getItemsByCategory = (categoryId: string) => {
  const category = premiumLibraryCategories.find(
    (cat) => cat.id === categoryId,
  );
  return category?.items || [];
};

export const getAllPremiumItems = () => {
  return premiumLibraryCategories.flatMap((category) =>
    category.items.filter((item) => item.tier === "premium"),
  );
};

export const getAllBaseItems = () => {
  return premiumLibraryCategories.flatMap((category) =>
    category.items.filter((item) => item.tier === "base"),
  );
};
